// components/LeadSearchInput.jsx
import React, { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const LeadSearchInput = ({
  value,
  onChange,
  placeholder = "Search leads...",
}) => {
  const { isDarkMode } = useTheme();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && value.length >= 2) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value]);

  const fetchSuggestions = async (query) => {
    try {
      setLoading(true);
      const response = await apiService.getLeadSearchSuggestions(query, 10);
      setSuggestions(response.data || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion.label);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "name":
        return "👤";
      case "email":
        return "📧";
      case "company":
        return "🏢";
      default:
        return "🔍";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "name":
        return "Name";
      case "email":
        return "Email";
      case "company":
        return "Company";
      default:
        return "Lead";
    }
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400 focus:border-gray-600"
              : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-400"
          }`}
        />
        <Search
          size={16}
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
        {value && (
          <button
            onClick={handleClear}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={16} />
          </button>
        )}
        {loading && (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 animate-spin"
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-2 border rounded-lg shadow-xl max-h-80 overflow-y-auto ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="py-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.id}-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 group ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                }`}
              >
                <span className="text-xl">{getTypeIcon(suggestion.type)}</span>
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium truncate transition-colors ${
                      isDarkMode
                        ? "text-white group-hover:text-blue-400"
                        : "text-gray-900 group-hover:text-blue-600"
                    }`}
                  >
                    {suggestion.label}
                  </div>
                  <div
                    className={`text-xs mt-0.5 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {getTypeLabel(suggestion.type)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {showSuggestions &&
        !loading &&
        value.length >= 2 &&
        suggestions.length === 0 && (
          <div
            ref={dropdownRef}
            className={`absolute z-50 w-full mt-2 border rounded-lg shadow-xl ${
              isDarkMode
                ? "bg-[#13161B] border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`py-4 px-4 text-center text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              No suggestions found
            </div>
          </div>
        )}
    </div>
  );
};

export default LeadSearchInput;
